import { Button, Modal, Popover, Radio } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgKebab } from '@common/icon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { AuthMutation } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { SELECTOR } from '@/constants';

import { useDeleteCommentMutation } from './_hooks/useDeleteCommentMutation';

export interface CommentMenuProps {
  isMyComment: boolean;
  commentId: number;
  onClickEditButton?: (commentId: number) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
}

const REPORT_REASONS = [
  '홍보성 광고글',
  '음란성, 선정성 글',
  '욕설',
  '개인정보 노출',
  '반복 게시글',
  '기타',
];

// 신고 폼은 ResponsiveDialog 안에서, 삭제 확인은 Modal로 분리.
type View = 'report' | 'delete';

export const CommentMenu = ({
  isMyComment,
  commentId,
  onClickEditButton,
  onDeleteSuccess,
}: CommentMenuProps) => {
  const [view, setView] = useState<View | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const close = () => {
    setView(null);
    setSelectedReason(undefined);
  };

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const reportMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/wello-comment-report'](),
    onSuccess: async (_, { body }) => {
      await queryClient.invalidateQueries();

      scroller.scrollTo(
        String(SELECTOR.COMMENT_PREFIX + body?.welloCommentId),
        { offset: -100 },
      );
      close();
      toast({ message: '댓글이 신고되었습니다.' });
    },
  });

  const deleteMutation = useDeleteCommentMutation({
    onDeleteSuccess: () => onDeleteSuccess?.(commentId),
  });

  return (
    <>
      <Popover.Root
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        floatingPadding={8}
        placement="bottom-end"
      >
        <Popover.Trigger className="flex size-26 items-center justify-center">
          <SvgKebab className="size-14 text-Gray-700" />
        </Popover.Trigger>
        <Popover.Content className="flex flex-col px-0 py-12 font-400 text-Gray-900 typo-body3">
          {isMyComment ? (
            <>
              {onClickEditButton ? (
                <button
                  type="button"
                  className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                  onClick={() => {
                    setIsPopoverOpen(false);
                    onClickEditButton(commentId);
                  }}
                >
                  수정
                </button>
              ) : null}
              <button
                type="button"
                className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                onClick={() => {
                  setIsPopoverOpen(false);
                  setView('delete');
                }}
              >
                삭제
              </button>
            </>
          ) : (
            <button
              type="button"
              className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
              onClick={() => {
                setIsPopoverOpen(false);
                setView('report');
              }}
            >
              신고
            </button>
          )}
        </Popover.Content>
      </Popover.Root>
      <ResponsiveDialog
        opened={view === 'report'}
        onClose={close}
        drawerType="bottom"
      >
        <form
          className="flex flex-col gap-24"
          onSubmit={(e) => {
            e.preventDefault();
            if (!selectedReason) return;

            reportMutation.mutate({
              body: { welloCommentId: commentId, contents: selectedReason },
            });
          }}
        >
          <h2 className="font-700 text-Gray-900 typo-body1">
            신고하는 이유를 선택해주세요.
          </h2>
          <Radio.Group
            className="flex-col gap-20"
            value={selectedReason}
            onChange={setSelectedReason}
          >
            {REPORT_REASONS.map((reason) => (
              <Radio.Item
                key={reason}
                className="justify-start typo-body3"
                value={reason}
              >
                {reason}
              </Radio.Item>
            ))}
          </Radio.Group>
          <Button
            className="w-full"
            disabled={!selectedReason}
            loading={reportMutation.isPending}
            type="submit"
          >
            선택 완료
          </Button>
        </form>
      </ResponsiveDialog>
      <Modal opened={view === 'delete'} onClose={close}>
        <div className="flex flex-col gap-24">
          <div className="flex flex-col gap-8">
            <p className="font-700 text-Gray-900 typo-body2">댓글 삭제</p>
            <p className="font-400 text-Gray-700 typo-body3">
              삭제한 댓글은 복구할 수 없어요.
            </p>
          </div>
          <div className="flex items-center gap-8">
            <Button
              className="flex-1"
              dimensions="H48"
              theme="sub-fill"
              onClick={close}
            >
              취소
            </Button>
            <Button
              className="flex-1"
              dimensions="H48"
              loading={deleteMutation.isPending}
              theme="primary-fill"
              onClick={() => {
                deleteMutation.mutate(
                  { path: { welloCommentId: commentId } },
                  { onSettled: close },
                );
              }}
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
